<template>
  <div>
    <p class="crm-kicker">Stand live</p>
    <h1 class="crm-title">Journée</h1>
    <p class="crm-hint" v-if="eventLabel">{{ eventLabel }}</p>
    <p class="crm-hint" v-if="stats && stats.message">{{ stats.message }}</p>
    <p v-if="refreshing" class="crm-loader-inline" style="margin-bottom:10px">Mise à jour des stats…</p>

    <div v-if="loading && !stats" class="crm-loader-block">
      <div class="crm-spinner"></div>
      <p class="crm-hint" style="margin:0">Chargement de la journée…</p>
    </div>

    <template v-else>
      <div class="crm-card" v-if="!salonModeResolved && archivedEvents.length">
        <label class="crm-label">Archives</label>
        <select class="crm-input" v-model="archiveId" @change="onArchivePick">
          <option value="">Salon affiché (auto)</option>
          <option v-for="e in archivedEvents" :key="e.id" :value="e.id">
            {{ e.name }} · {{ formatDateRange(e.start_date, e.end_date) }}
          </option>
        </select>
      </div>

      <div class="crm-card" v-if="dayChips.length">
        <div class="crm-day-seg">
          <button type="button" class="crm-chip" :class="{ on: !day }" @click="setDay(null)">
            {{ dayChips.length > 1 ? dayChips.length + ' jours' : 'Journée' }}
          </button>
          <button
            v-for="d in dayChips"
            :key="d"
            type="button"
            class="crm-chip"
            :class="{ on: day === d }"
            @click="setDay(d)"
          >{{ formatDayChip(d) }}</button>
        </div>
      </div>

      <div class="crm-card" v-if="accountOptions.length">
        <label class="crm-label">Compte</label>
        <div class="crm-day-seg">
          <button
            type="button"
            class="crm-chip"
            :class="{ on: !accountFilter }"
            @click="accountFilter = null"
          >Tous ({{ accountOptionsTotal }})</button>
          <button
            v-for="a in accountOptions"
            :key="a.id"
            type="button"
            class="crm-chip"
            :class="{ on: accountFilter === a.id }"
            @click="accountFilter = a.id"
          >{{ a.id }} ({{ a.count }})</button>
        </div>
      </div>

      <div class="crm-hero-navy" v-if="stats">
        <p class="crm-kicker">{{ heroKicker }}</p>
        <div class="crm-hero-count">{{ pageView.visitors }}</div>
        <p class="crm-hint" style="margin-top:8px;margin-bottom:0">
          {{ heroHint }}
        </p>
      </div>

      <div class="crm-grid-2" v-if="stats" style="margin-bottom:12px">
        <div class="crm-stat"><strong>{{ pageView.nouveaux }}</strong><span>Nouveaux</span></div>
        <div class="crm-stat"><strong>{{ pageView.clients }}</strong><span>Clients</span></div>
        <div class="crm-stat"><strong>{{ pageView.prospects }}</strong><span>Prospects</span></div>
        <div class="crm-stat"><strong>{{ pageView.rdv }}</strong><span>RDV</span></div>
        <div class="crm-stat"><strong>{{ pageView.median_duration_sec }}s</strong><span>Durée médiane</span></div>
        <div class="crm-stat"><strong>{{ pageView.prizeTotal }}</strong><span>Remises tirées</span></div>
      </div>

      <div class="crm-card" v-if="pageView.prizes.length">
        <label class="crm-label">Répartition remises</label>
        <div v-for="p in pageView.prizes" :key="p.percent" class="crm-list-row">
          <div class="crm-row-title">{{ p.percent }} %</div>
          <strong>{{ p.count }}</strong>
        </div>
      </div>

      <div class="crm-card" v-if="pageView.affluence.length">
        <label class="crm-label">Affluence horaire</label>
        <div v-for="a in pageView.affluence" :key="a.hour" class="crm-list-row">
          <div class="crm-row-title" style="min-width:40px">{{ a.hour }}h</div>
          <div class="crm-affluence-bar"><i :style="{ width: affluencePct(a.count) + '%' }"></i></div>
          <strong>{{ a.count }}</strong>
        </div>
      </div>

      <div class="crm-card tight" v-if="stats">
        <label class="crm-label">Contacts</label>
        <div
          v-for="c in pageView.contacts"
          :key="c.prospect_id || c.id"
          class="crm-list-row crm-contact-group"
          style="cursor:pointer"
          @click="openContact(c)"
        >
          <div style="flex:1;min-width:0">
            <div class="crm-row-title">{{ c.label || '—' }}</div>
            <div class="crm-row-meta">{{ groupMeta(c) }}</div>
            <div class="crm-contact-actions">
              <span v-for="act in c.actions" :key="act.key" class="crm-badge">{{ act.label }}</span>
            </div>
          </div>
        </div>
        <p v-if="!pageView.contacts.length" class="crm-hint" style="margin:8px 0">Aucun contact encore</p>
      </div>

      <p v-if="!loading && !stats" class="crm-hint">Impossible de charger la journée.</p>
    </template>
  </div>
</template>

<script>
import {
  getCachedEvents,
  setCachedEvents,
  getCachedJournee,
  setCachedJournee
} from './crmSalonCache'
import {
  activityTimestamp,
  buildParisAffluence,
  formatParisDateTime,
  formatDateRange,
  formatDayChip,
  toParisInstant,
  withFrom
} from './crmSalonUtils'

const ACTION_ORDER = {
  create: 1,
  visite: 2,
  echange: 3,
  rdv: 4,
  proposition: 5,
  qualify: 6,
  rdv_booked: 7,
  prize: 8,
  pipeline: 9
}

export default {
  name: 'CrmSalonJournee',
  inject: {
    crmSalonMode: { default: null }
  },
  data() {
    const cachedStats = getCachedJournee(null, null)
    return {
      day: null,
      eventId: null,
      archiveId: '',
      stats: cachedStats,
      archivedEvents: [],
      loading: !cachedStats,
      refreshing: false,
      accountFilter: null
    }
  },
  computed: {
    salonModeResolved() {
      if (this.crmSalonMode) return !!this.crmSalonMode.salonMode
      return true
    },
    dayChips() {
      const e = this.stats && this.stats.event
      return (e && e.days) || []
    },
    eventLabel() {
      const e = this.stats && this.stats.event
      if (!e) return ''
      const range =
        e.start_date === e.end_date
          ? formatDayChip(e.start_date)
          : formatDayChip(e.start_date) + ' – ' + formatDayChip(e.end_date)
      return [e.name, e.city, range].filter(Boolean).join(' · ')
    },
    heroKicker() {
      if (!this.stats) return 'Contacts'
      if (this.accountFilter) return 'Contacts · ' + this.accountFilter
      return this.stats.is_live ? 'Contacts live' : 'Contacts'
    },
    heroHint() {
      if (!this.stats) return ''
      if (this.accountFilter) return 'Filtré sur le compte ' + this.accountFilter
      if (this.stats.is_live) return 'Compteur live stand'
      if (this.stats.event && this.stats.event.is_future) return 'Salon à venir'
      return 'Historique'
    },
    contactRows() {
      if (!this.stats) return []
      const raw = (this.stats.contacts && this.stats.contacts.length)
        ? this.stats.contacts
        : (this.stats.visits || []).map((v) => ({
          id: 'visit-' + v.id,
          prospect_id: v.prospect_id,
          label: v.label,
          societe: v.societe,
          action: this.passageLabel(v.passage),
          action_key: v.passage || 'visite',
          at: v.visited_at,
          updated_at: v.updated_at || null,
          visited_at: v.visited_at,
          created_at: v.created_at || null,
          created_by: v.created_by,
          origin: v.origin
        }))
      return raw.map((r) => ({
        ...r,
        at: activityTimestamp(r)
      }))
    },
    filteredRows() {
      if (!this.accountFilter) return this.contactRows
      const acc = this.accountFilter
      return this.contactRows.filter((r) => this.accountLabel(r.created_by) === acc)
    },
    activityRows() {
      return this.filteredRows
    },
    filteredVisits() {
      const visits = (this.stats && this.stats.visits) || []
      if (!this.accountFilter) return visits
      const acc = this.accountFilter
      return visits.filter((v) => this.accountLabel(v.created_by) === acc)
    },
    groupedFromRows() {
      return this.buildGrouped(this.activityRows)
    },
    accountOptions() {
      const counts = {}
      this.buildGrouped(this.contactRows).forEach((g) => {
        ;(g.accounts || []).forEach((acc) => {
          counts[acc] = (counts[acc] || 0) + 1
        })
      })
      return Object.keys(counts)
        .sort()
        .map((id) => ({ id, count: counts[id] }))
    },
    accountOptionsTotal() {
      return this.buildGrouped(this.contactRows).length
    },
    pageView() {
      if (!this.stats) {
        return {
          visitors: 0,
          nouveaux: 0,
          clients: 0,
          prospects: 0,
          rdv: 0,
          median_duration_sec: 0,
          prizeTotal: 0,
          prizes: [],
          affluence: [],
          contacts: []
        }
      }

      const rows = this.activityRows
      const contacts = this.groupedFromRows
      const affluence = buildParisAffluence(rows)

      // Sans filtre compte : totaux API, mais affluence toujours recalculée en heure Paris
      if (!this.accountFilter) {
        const prizes = this.stats.prizes || []
        return {
          visitors: Number(this.stats.visitors) || 0,
          nouveaux: Number(this.stats.nouveaux) || 0,
          clients: Number(this.stats.clients) || 0,
          prospects: Number(this.stats.prospects) || 0,
          rdv: Number(this.stats.rdv) || 0,
          median_duration_sec: Number(this.stats.median_duration_sec) || 0,
          prizeTotal: prizes.reduce((s, p) => s + (Number(p.count) || 0), 0),
          prizes,
          affluence,
          contacts
        }
      }

      const uniqueIds = {}
      contacts.forEach((c) => {
        if (c.prospect_id) uniqueIds[String(c.prospect_id)] = true
      })

      const originByProspect = {}
      this.filteredVisits.forEach((v) => {
        const pid = String(v.prospect_id || '')
        if (!pid) return
        if (!originByProspect[pid]) originByProspect[pid] = v.origin || 'nouveau'
      })

      let nouveaux = 0
      let clients = 0
      let prospects = 0
      Object.keys(uniqueIds).forEach((pid) => {
        const origin = originByProspect[pid] || 'nouveau'
        if (origin === 'actif') clients++
        else if (origin === 'nouveau') nouveaux++
        else prospects++
      })

      let rdv = 0
      const prizeMap = { 10: 0, 15: 0, 20: 0, 25: 0 }
      rows.forEach((r) => {
        const key = r.action_key || ''
        if (key === 'rdv' || key === 'rdv_booked') rdv++
        if (key === 'prize') {
          const m = String(r.action || '').match(/(\d+)/)
          const pct = m ? String(Number(m[1])) : ''
          if (prizeMap[pct] != null) prizeMap[pct]++
        }
      })

      const durations = this.filteredVisits
        .map((v) => Number(v.duration_sec))
        .filter((n) => n > 0)
        .sort((a, b) => a - b)
      let median = 0
      if (durations.length) {
        const mid = Math.floor(durations.length / 2)
        median = durations.length % 2
          ? durations[mid]
          : Math.round((durations[mid - 1] + durations[mid]) / 2)
      }

      const prizes = [10, 15, 20, 25].map((percent) => ({
        percent,
        count: prizeMap[percent]
      }))

      return {
        visitors: Object.keys(uniqueIds).length,
        nouveaux,
        clients,
        prospects,
        rdv,
        median_duration_sec: median,
        prizeTotal: prizes.reduce((s, p) => s + p.count, 0),
        prizes,
        affluence,
        contacts
      }
    },
    maxAffluence() {
      const list = this.pageView.affluence || []
      if (!list.length) return 1
      return Math.max.apply(null, list.map((a) => Number(a.count) || 0)) || 1
    }
  },
  mounted() {
    this.hydrateArchivesFromCache()
    this.load({ soft: !!this.stats })
    if (!this.salonModeResolved) this.loadArchives({ soft: !!this.archivedEvents.length })
  },
  watch: {
    salonModeResolved(v) {
      if (!v) this.loadArchives({ soft: !!this.archivedEvents.length })
    }
  },
  methods: {
    buildGrouped(rows) {
      const map = {}
      const list = []
      ;(rows || []).forEach((row) => {
        const pid = String(row.prospect_id || row.id || '')
        if (!pid) return
        if (!map[pid]) {
          map[pid] = {
            id: pid,
            prospect_id: row.prospect_id,
            label: row.label || '—',
            societe: row.societe || '',
            at: row.at,
            accounts: [],
            actions: [],
            _actionKeys: {}
          }
          list.push(map[pid])
        }
        const g = map[pid]
        if (row.label && (!g.label || g.label === '—')) g.label = row.label
        if (row.societe && !g.societe) g.societe = row.societe
        const stamp = activityTimestamp(row)
        const stampMs = toParisInstant(stamp)
        const prevMs = toParisInstant(g.at)
        if (stampMs != null && (prevMs == null || stampMs > prevMs)) g.at = stamp
        const account = this.accountLabel(row.created_by)
        if (account && account !== '—' && g.accounts.indexOf(account) === -1) {
          g.accounts.push(account)
        }
        const key = row.action_key || row.action || 'action'
        const label = row.action || this.passageLabel(key)
        const actionId = key + '|' + account
        if (!g._actionKeys[actionId]) {
          g._actionKeys[actionId] = true
          g.actions.push({
            key: actionId,
            actionKey: key,
            label,
            account,
            sort: ACTION_ORDER[key] != null ? ACTION_ORDER[key] : 50
          })
        }
      })
      list.forEach((g) => {
        g.actions.sort((a, b) => a.sort - b.sort)
        delete g._actionKeys
      })
      list.sort((a, b) => (toParisInstant(b.at) || 0) - (toParisInstant(a.at) || 0))
      return list
    },
    formatDayChip,
    formatDateRange,
    passageLabel(passage) {
      const map = {
        visite: 'Visite',
        echange: 'Échange',
        rdv: 'Souhaite un RDV',
        proposition: 'Proposition'
      }
      const key = passage || 'visite'
      return map[key] || key
    },
    accountLabel(email) {
      if (!email) return '—'
      const s = String(email)
      const at = s.indexOf('@')
      return at > 0 ? s.slice(0, at) : s
    },
    groupMeta(c) {
      const parts = []
      if (c.societe) parts.push(c.societe)
      if (c.at) parts.push(formatParisDateTime(c.at))
      if (c.accounts && c.accounts.length) parts.push(c.accounts.join(', '))
      return parts.join(' · ') || '—'
    },
    openContact(c) {
      if (!c || !c.prospect_id) return
      this.$router.push(withFrom('/crm-salon/fiche/' + c.prospect_id, this.$route.fullPath))
    },
    affluencePct(count) {
      return Math.round(((Number(count) || 0) / this.maxAffluence) * 100)
    },
    hydrateArchivesFromCache() {
      const all = getCachedEvents(true)
      if (all && all.length) {
        this.archivedEvents = all.filter(e => e.is_past || e.archived)
      }
    },
    setDay(d) {
      this.day = d
      this.accountFilter = null
      const cached = getCachedJournee(this.eventId, this.day)
      if (cached) this.stats = cached
      this.load({ soft: !!cached })
    },
    onArchivePick() {
      this.eventId = this.archiveId || null
      this.day = null
      this.accountFilter = null
      const cached = getCachedJournee(this.eventId, this.day)
      if (cached) this.stats = cached
      this.load({ soft: !!cached })
    },
    async loadArchives(opts) {
      const soft = !!(opts && opts.soft)
      try {
        const { data } = await this.$apollo.query({
          client: 'AUTH',
          query: require('src/graphql/auth/crmSalon/crmSalonEvents.gql'),
          variables: { include_archived: true },
          fetchPolicy: soft ? 'cache-first' : 'network-only'
        })
        const all = data.crmSalonEvents || []
        setCachedEvents(true, all)
        this.archivedEvents = all.filter(e => e.is_past || e.archived)
      } catch (e) {
        if (!this.archivedEvents.length) this.archivedEvents = []
      }
    },
    async load(opts) {
      const soft = !!(opts && opts.soft)
      if (soft) this.refreshing = true
      else this.loading = true
      try {
        const { data } = await this.$apollo.query({
          client: 'AUTH',
          query: require('src/graphql/auth/crmSalon/crmSalonJournee.gql'),
          variables: {
            day: this.day,
            event_id: this.eventId || null
          },
          fetchPolicy: 'network-only'
        })
        this.stats = data.crmSalonJournee
        setCachedJournee(this.eventId, this.day, this.stats)
      } catch (e) {
        if (!this.stats) this.stats = null
      } finally {
        this.loading = false
        this.refreshing = false
      }
    }
  }
}
</script>
